import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon, InfoIcon } from '@phosphor-icons/react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import {
  formatCurrency,
  formatDate,
  assignCategoryColors,
  stripCategoryEmoji,
  splitCategoryEmoji,
  startOfDay,
  addDays,
  buildOccurrences,
} from '@budget/core';
import type {
  Tri,
  CategoryRow,
  BudgetCategory,
  DashboardData,
  RetirementData,
  Bill,
  Gift,
} from '@budget/core';
import { useDashboard } from '../data/useDashboard';
import { usePlaidAutoSync } from '../data/usePlaidAutoSync';
import { useBudgetActuals } from '../data/useBudgetActuals';
import { currentEtMonth } from '../lib/dates';
import { useTransactionsEnabled } from '../data/useTransactionsEnabled';
import { usePlaidItems } from '../data/usePlaidItems';
import { useHousehold } from '../auth/useHousehold';
import { useScope } from '../hooks/useScope';
import type { TransactionsScope } from '../data/useTransactions';
import { PersonBreakdown } from '../components/PersonBreakdown';
import { type Scope } from '../components/ScopeToggle';
import { CardHeader } from '../components/CardHeader';
import { CategoryProgressRing } from '../components/CategoryProgressRing';
import { getCategoryIcon } from '../components/CategoryIcon';
import { NetWorthHistoryChart } from '../components/NetWorthHistoryChart';
import { Tooltip as InfoTooltip } from '../components/Tooltip';
import { RetirementGrowthChart } from '../components/RetirementGrowthChart';
import { DonutCenterEmoji } from '../components/DonutCenterEmoji';
import { ThirdPartyLogo } from '../components/ThirdPartyLogos';
import { CreditStrip } from '../components/CreditStrip';
import { useChartReady } from '../hooks/useChartReady';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { BreakdownLegend } from '../components/BreakdownLegend';
import { getPersonName } from '../config/personNames';
import clsx from 'clsx';

function scopeLabel(scope: Scope): string {
  if (scope === 'household') return 'household';
  return getPersonName(scope);
}

function fmt0(n: number): string {
  return formatCurrency(n, 'USD', 'en-US', 0);
}

type StatCell = {
  label: string;
  value: number;
  valueText?: string;
  suffix?: string;
  tone?: 'pos' | 'neg';
  sub?: string;
  breakdown?: { dakota: number; natalia: number };
  href?: string;
};

function StatRow({ cells }: { cells: StatCell[] }) {
  return (
    <div className="grid grid-cols-3 gap-3 max-md:grid-cols-1">
      {cells.map((c) => {
        const inner = (
          <>
            <span className="stat-label">{c.label}</span>
            <span
              className={clsx(
                'stat-value tabular',
                c.tone === 'pos' && 'delta-pos',
                c.tone === 'neg' && 'delta-neg'
              )}
            >
              {c.valueText ?? fmt0(c.value)}
              {c.suffix && (
                <span className="text-fg-tertiary text-sm font-medium ml-2 tracking-normal">
                  {c.suffix}
                </span>
              )}
            </span>
            {c.breakdown ? (
              <PersonBreakdown
                dakota={c.breakdown.dakota}
                natalia={c.breakdown.natalia}
                digits={0}
              />
            ) : c.sub ? (
              <span className="stat-meta">{c.sub}</span>
            ) : null}
          </>
        );

        if (c.href) {
          return (
            <Link
              key={c.label}
              to={c.href}
              className="stat-card group relative no-underline text-inherit"
            >
              <ArrowUpRightIcon
                size={20}
                weight="bold"
                className="absolute top-6 right-6 text-fg-tertiary group-hover:text-fg transition-colors"
              />
              {inner}
            </Link>
          );
        }

        return (
          <div key={c.label} className="stat-card">
            {inner}
          </div>
        );
      })}
    </div>
  );
}

function NetWorthStrip({
  net,
  assets,
  liabilities,
}: {
  net: Tri;
  assets: Tri;
  liabilities: Tri;
}) {
  return (
    <StatRow
      cells={[
        {
          label: 'Net worth',
          value: net.household,
          breakdown: { dakota: net.dakota, natalia: net.natalia },
          href: '/net-worth',
        },
        {
          label: 'Assets',
          value: assets.household,
          breakdown: { dakota: assets.dakota, natalia: assets.natalia },
          href: '/net-worth',
        },
        {
          label: 'Liabilities',
          value: liabilities.household,
          breakdown: { dakota: liabilities.dakota, natalia: liabilities.natalia },
          href: '/net-worth',
        },
      ]}
    />
  );
}

// Wraps the household-net-worth hero (eyebrow + balance + chart) and the
// 3-up snapshot strip below it into a single card group so they read as
// one section. Mirrors the standalone hero on the Net Worth page so the
// dashboard surfaces the same projection at a glance.
function NetWorthGroup({
  netWorth,
  assets,
  liabilities,
  retirement,
  monthlyDebtPaydown,
  monthlySavings,
}: {
  netWorth: Tri;
  assets: Tri;
  liabilities: Tri;
  retirement: RetirementData;
  monthlyDebtPaydown: number;
  monthlySavings: Tri;
}) {
  const { scope } = useScope();
  return (
    <div className="space-y-5">
      <div className="card">
        <CardHeader
          title="Net worth over time"
          subtitle={
            <span className="inline-flex items-center gap-1.5">
              Historical and projected net worth over time
              <InfoTooltip
                wide
                content="Projection adds your scheduled retirement contributions, non-retirement savings, and debt paydown to today's balance, compounding at an 8% annual return."
              >
                <InfoIcon
                  size={14}
                  weight="regular"
                  className="text-fg-tertiary cursor-help"
                  tabIndex={0}
                  aria-label="About the net-worth projection"
                />
              </InfoTooltip>
            </span>
          }
        />
        <NetWorthHistoryChart
          flush
          scope={scope}
          currentNetWorth={netWorth}
          retirement={retirement}
          monthlyDebtPaydown={monthlyDebtPaydown}
          monthlySavings={monthlySavings}
        />
      </div>
      <NetWorthStrip net={netWorth} assets={assets} liabilities={liabilities} />
      <CreditStrip />
    </div>
  );
}

// Wraps the retirement chart + 3-up strip so a single scope toggle on the
// chart card applies to the strip below it. Both children read the same
// lifted state. Goal line is 50× annual expenses, rescoped per toggle so
// the target moves with the projection.
function RetirementGroup({
  retirement,
  annualExpenses,
}: {
  retirement: RetirementData;
  annualExpenses: Tri;
}) {
  const { scope } = useScope();
  const annual = annualExpenses[scope];
  // Stack the goal label onto two lines on mobile (and abbreviate "expenses"
  // → "exp.") so the two goal labels don't overlap in the narrow chart.
  const isMobile = useMediaQuery('(max-width: 767.98px)');
  const goals = isMobile
    ? [
        { value: annual * 25, label: 'Goal 1:\n25× exp.' },
        { value: annual * 50, label: 'Goal 2:\n50× exp.' },
      ]
    : [
        { value: annual * 25, label: 'Goal 1: 25× expenses' },
        { value: annual * 50, label: 'Goal 2: 50× expenses' },
      ];
  return (
    <div className="space-y-5">
      <RetirementGrowthChart
        dakota={retirement.dakota}
        natalia={retirement.natalia}
        scope={scope}
        title="Projected retirement growth"
        subtitle="End-of-year balance per the spreadsheet's model"
        height={300}
        goals={goals}
      />
      <RetirementStrip retirement={retirement} scope={scope} />
    </div>
  );
}

function RetirementStrip({
  retirement,
  scope,
}: {
  retirement: RetirementData;
  scope: Scope;
}) {
  // Match the retirement HeroCard's logic so the dashboard numbers and the
  // /retirement landing page agree. Both projections are indexed by year and
  // we look up each person at the union's first/last year — undefined means
  // their projection doesn't reach that year, which contributes 0.
  const dKey = new Map(retirement.dakota.years.map((y) => [y.year, y]));
  const nKey = new Map(retirement.natalia.years.map((y) => [y.year, y]));
  const allYears = Array.from(new Set([...dKey.keys(), ...nKey.keys()])).sort(
    (a, b) => a - b
  );
  const firstYear = allYears[0];
  const projectionYear = 2058;

  const today = {
    dakota: dKey.get(firstYear)?.totals.initial ?? 0,
    natalia: nKey.get(firstYear)?.totals.initial ?? 0,
  };
  const projected = {
    dakota: dKey.get(projectionYear)?.totals.finalAmount ?? 0,
    natalia: nKey.get(projectionYear)?.totals.finalAmount ?? 0,
  };
  const safeIncome = {
    dakota: dKey.get(projectionYear)?.totals.withdrawalRule ?? 0,
    natalia: nKey.get(projectionYear)?.totals.withdrawalRule ?? 0,
  };
  // Scope-aware top number, but the per-person breakdown chips stay visible
  // on every scope so the card always shows how Dakota and Natalia split.
  const valueOf = (t: { dakota: number; natalia: number }) =>
    scope === 'dakota' ? t.dakota : scope === 'natalia' ? t.natalia : t.dakota + t.natalia;

  return (
    <StatRow
      cells={[
        {
          label: 'Retirement today',
          value: valueOf(today),
          breakdown: today,
          href: '/retirement',
        },
        {
          label: `Projected at ${projectionYear}`,
          value: valueOf(projected),
          breakdown: projected,
          href: '/retirement',
        },
        {
          label: 'Safe income (4%)',
          value: valueOf(safeIncome),
          suffix: '/yr',
          breakdown: safeIncome,
          href: '/retirement',
        },
      ]}
    />
  );
}

function MonthlyTotals({
  outTotal,
  inTotal,
  gap,
  scope,
}: {
  outTotal: Tri;
  inTotal: Tri;
  gap: Tri;
  scope: Scope;
}) {
  // Top number reflects the active scope; the per-person chips below stay
  // visible across all scopes so the card always shows the Dakota/Natalia
  // split alongside the headline number.
  return (
    <StatRow
      cells={[
        {
          label: 'Monthly in (net)',
          value: inTotal[scope],
          suffix: '/mo',
          breakdown: { dakota: inTotal.dakota, natalia: inTotal.natalia },
          href: '/income',
        },
        {
          label: 'Monthly out',
          value: outTotal[scope],
          suffix: '/mo',
          breakdown: { dakota: outTotal.dakota, natalia: outTotal.natalia },
          href: '/spending',
        },
        {
          label: 'Monthly remaining',
          value: gap[scope],
          suffix: '/mo',
          breakdown: { dakota: gap.dakota, natalia: gap.natalia },
          href: '/spending?tab=remaining',
        },
      ]}
    />
  );
}

const TOOLTIP_STYLE = {
  background: 'var(--berg-bg-elevated)',
  border: '1px solid var(--berg-border-strong)',
  borderRadius: 12,
  fontSize: 12,
  color: 'var(--berg-text)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
};

const TOOLTIP_LABEL_STYLE = { color: 'var(--berg-text)', fontWeight: 600 };
const TOOLTIP_ITEM_STYLE = { color: 'var(--berg-text)' };

function BillAlerts({
  bills,
  budgetCategories,
}: {
  bills: Bill[];
  budgetCategories: BudgetCategory[];
}) {
  // Returns null when nothing is due in the window — the dashboard then shows
  // no section at all, so it behaves as an actual alert (only present when
  // there's something to alert about). Income rows (paydays) are filtered
  // out — this strip is "what's owed", not a cash-flow timeline.
  const today = useMemo(() => startOfDay(new Date()), []);
  const horizon = useMemo(() => addDays(today, 14), [today]);
  const { scope } = useScope();

  // Mirrors BillsPage's scope logic: filter out bills the active person
  // isn't on the hook for, and rescale shared bills by that person's share
  // of the matching Cash Out item. Without this, the dashboard would show
  // joint amounts even when the user has scoped to a single person.
  const cashOutItemsByName = useMemo(() => {
    const map = new Map<string, { household: number; dakota: number; natalia: number }>();
    for (const c of budgetCategories) {
      for (const item of c.items) {
        if (!item.name) continue;
        map.set(item.name, {
          household: item.household,
          dakota: item.dakota,
          natalia: item.natalia,
        });
      }
    }
    return map;
  }, [budgetCategories]);

  const scopedBills = useMemo(() => {
    const noIncome = bills.filter((b) => b.type !== 'income');
    if (scope === 'household') return noIncome;
    return noIncome
      .filter((b) => b.responsible === scope || b.responsible === 'shared')
      .map((b) => {
        if (b.responsible === scope) return b;
        const match = b.cashOutItem ? cashOutItemsByName.get(b.cashOutItem) : null;
        if (!match || match.household <= 0) return b;
        const ratio =
          scope === 'dakota'
            ? match.dakota / match.household
            : match.natalia / match.household;
        return { ...b, amount: b.amount * ratio };
      });
  }, [bills, scope, cashOutItemsByName]);

  const upcoming = useMemo(
    () => buildOccurrences(scopedBills, today, horizon),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopedBills, today.getTime(), horizon.getTime()]
  );

  // Edge fades only show when there's actually more in that direction. We
  // recompute on scroll, on resize, and once on mount (after layout settles).
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasLeft, setHasLeft] = useState(false);
  const [hasRight, setHasRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      // 1px tolerance for sub-pixel rounding so the fade fully clears at the edge.
      setHasLeft(el.scrollLeft > 1);
      setHasRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [upcoming.length]);

  if (upcoming.length === 0) return null;

  const total = upcoming.reduce((sum, occ) => sum + occ.bill.amount, 0);

  return (
    <div className="card flex flex-col gap-2 sm:gap-4">
      <div className="flex items-start justify-between gap-3">
        <CardHeader
          title="Due in the next 2 weeks"
          subtitle={
            <>
              {upcoming.length} {upcoming.length === 1 ? 'bill' : 'bills'} ·{' '}
              <span className="tabular text-fg-secondary">
                {fmt0(total)}
              </span>
            </>
          }
        />
        <Link
          to="/spending?tab=bills"
          aria-label="View all bills"
          className="text-fg-tertiary hover:text-fg transition-colors flex-none mt-0.5"
        >
          <ArrowUpRightIcon size={20} weight="bold" />
        </Link>
      </div>
      {/* Horizontal scroll strip — fixed-width cards snap to the left edge.
          Negative margins + matching padding bleed the strip to the card's
          inner edges so the first/last cards aren't cramped against them.
          The relative wrapper hosts the gradient fade overlays. */}
      <div className="relative -mx-4 sm:-mx-6 -mb-2">
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 px-4 sm:px-6 scroll-pl-4 sm:scroll-pl-6 scroll-pr-4 sm:scroll-pr-6 [scrollbar-width:thin]"
          role="list"
        >
        {upcoming.map((occ, i) => {
          const days = Math.round(
            (occ.date.getTime() - today.getTime()) / 86_400_000
          );
          const dayLabel = occ.date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
          const relative =
            days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `In ${days} days`;
          const isToday = days === 0;
          const isTomorrow = days === 1;
          return (
            <Link
              key={`${occ.bill.name}-${i}`}
              to="/spending?tab=bills"
              role="listitem"
              className="snap-start shrink-0 w-36 sm:w-44 rounded-2xl border border-border bg-elevated p-3 sm:p-4 flex flex-col gap-2 sm:gap-3 no-underline text-inherit transition-colors hover:bg-overlay"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="sm:hidden w-7 h-7 flex-none flex items-center justify-center">
                  <ThirdPartyLogo location={occ.bill.name} size={24} />
                </span>
                <span className="hidden sm:flex w-9 h-9 flex-none items-center justify-center">
                  <ThirdPartyLogo location={occ.bill.name} size={32} />
                </span>
                <span
                  className={clsx(
                    'text-[0.625rem] sm:text-[0.6875rem] uppercase tracking-[0.08em] font-semibold px-1.5 sm:px-2 py-0.5 rounded-full tabular',
                    isToday
                      ? 'bg-green-soft text-green'
                      : isTomorrow
                        ? 'bg-amber-light/15 text-amber-light'
                        : 'bg-overlay text-fg-tertiary'
                  )}
                >
                  {relative}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 min-w-0">
                <div className="text-sm sm:text-base font-semibold truncate">{occ.bill.name}</div>
                <div className="text-[0.6875rem] sm:text-xs text-fg-tertiary truncate">
                  {dayLabel}
                </div>
              </div>
              {occ.bill.amount > 0 && (
                <div className="tabular text-base sm:text-lg font-semibold mt-auto">
                  {formatCurrency(occ.bill.amount)}
                </div>
              )}
            </Link>
          );
        })}
        </div>
        <div
          aria-hidden
          className={clsx(
            'pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-elevated to-transparent transition-opacity duration-150',
            hasLeft ? 'opacity-100' : 'opacity-0'
          )}
        />
        <div
          aria-hidden
          className={clsx(
            'pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-elevated to-transparent transition-opacity duration-150',
            hasRight ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
    </div>
  );
}

// Mirror of BillAlerts for gifts: surfaces anything in this month or next
// (calendar-wrapped at Dec → Jan) so the user can see what's coming up
// without leaving the dashboard. Returns null when there's nothing in the
// window so the section disappears entirely.
function GiftAlerts({ gifts }: { gifts: Gift[] }) {
  const upcoming = useMemo(() => {
    const currentMonth = currentEtMonth();
    const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
    return gifts
      .filter(
        (g) =>
          (g.month === currentMonth || g.month === nextMonth) &&
          g.household > 0,
      )
      .slice()
      .sort((a, b) => {
        if (a.month === b.month) return 0;
        // Current month first, then next month.
        if (a.month === currentMonth) return -1;
        if (b.month === currentMonth) return 1;
        return 0;
      });
  }, [gifts]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasLeft, setHasLeft] = useState(false);
  const [hasRight, setHasRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setHasLeft(el.scrollLeft > 1);
      setHasRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [upcoming.length]);

  if (upcoming.length === 0) return null;

  const total = upcoming.reduce((sum, g) => sum + g.household, 0);
  const currentMonth = currentEtMonth();
  const MONTH_LABELS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  return (
    <div className="card flex flex-col gap-2 sm:gap-4">
      <div className="flex items-start justify-between gap-3">
        <CardHeader
          title="Upcoming gifts"
          subtitle={
            <>
              {upcoming.length} {upcoming.length === 1 ? 'gift' : 'gifts'} over the next two months ·{' '}
              <span className="tabular text-fg-secondary">{fmt0(total)}</span>
            </>
          }
        />
        <Link
          to="/spending?tab=gifts"
          aria-label="View all gifts"
          className="text-fg-tertiary hover:text-fg transition-colors flex-none mt-0.5"
        >
          <ArrowUpRightIcon size={20} weight="bold" />
        </Link>
      </div>
      <div className="relative -mx-4 sm:-mx-6 -mb-2">
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 px-4 sm:px-6 scroll-pl-4 sm:scroll-pl-6 scroll-pr-4 sm:scroll-pr-6 [scrollbar-width:thin]"
          role="list"
        >
          {upcoming.map((g, i) => {
            const { emoji, name: bareName } = splitCategoryEmoji(g.name);
            const isNow = g.month === currentMonth;
            const monthLabel = g.month != null ? MONTH_LABELS[g.month - 1] : '';
            return (
              <Link
                key={`${g.name}-${i}`}
                to="/spending?tab=gifts"
                role="listitem"
                className="snap-start shrink-0 w-36 sm:w-44 rounded-2xl border border-border bg-elevated p-3 sm:p-4 flex flex-col gap-2 sm:gap-3 no-underline text-inherit transition-colors hover:bg-overlay"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="w-7 h-7 sm:w-9 sm:h-9 flex-none flex items-center justify-center text-2xl sm:text-3xl leading-none">
                    {emoji ?? '🎁'}
                  </span>
                  <span
                    className={clsx(
                      'text-[0.625rem] sm:text-[0.6875rem] uppercase tracking-[0.08em] font-semibold px-1.5 sm:px-2 py-0.5 rounded-full tabular',
                      isNow
                        ? 'bg-green-soft text-green'
                        : 'bg-yellow-soft text-yellow'
                    )}
                  >
                    {isNow ? 'Now' : 'Soon'}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <div className="text-sm sm:text-base font-semibold truncate">{bareName}</div>
                  <div className="text-[0.6875rem] sm:text-xs text-fg-tertiary truncate">
                    {g.recipient ? `${g.recipient} · ${monthLabel}` : monthLabel}
                  </div>
                </div>
                {g.household > 0 && (
                  <div className="tabular text-base sm:text-lg font-semibold mt-auto">
                    {formatCurrency(g.household)}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
        <div
          aria-hidden
          className={clsx(
            'pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-elevated to-transparent transition-opacity duration-150',
            hasLeft ? 'opacity-100' : 'opacity-0'
          )}
        />
        <div
          aria-hidden
          className={clsx(
            'pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-elevated to-transparent transition-opacity duration-150',
            hasRight ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
    </div>
  );
}

function CategoryDonut({
  rows,
  totals,
  scope,
}: {
  rows: CategoryRow[];
  totals: Tri;
  scope: Scope;
}) {
  const [hovered, setHoveredRaw] = useState<string | null>(null);
  // Hover-dim is gated until the entry animation finishes — mid-mount
  // hovers were canceling the donut's swoop-in.
  const ready = useChartReady();
  const setHovered = (next: string | null) => {
    if (ready) setHoveredRaw(next);
  };
  // Legend collapses to a "Show more" toggle only when the donut/legend
  // stack vertically (below `md`); side-by-side has room for the full list.
  const isStacked = useMediaQuery('(max-width: 767.98px)');
  const total = totals[scope];
  const colorMap = useMemo(
    () => assignCategoryColors(rows.map((r) => r.name)),
    [rows]
  );
  // Sort by the active scope's value so the legend reads largest-first for
  // whichever person is being viewed. Items the scope doesn't hit fall to
  // the bottom (still listed at $0).
  const sortedRows = [...rows].sort((a, b) => b[scope] - a[scope]);
  const data = sortedRows.map((r) => ({
    name: r.name,
    label: stripCategoryEmoji(r.name),
    value: r[scope],
    color: colorMap.get(r.name) ?? 'var(--berg-neutral)',
    percent: total > 0 ? (r[scope] / total) * 100 : 0,
  }));

  return (
    <div className="card">
      <CardHeader
        title="Where the money goes"
        subtitle={
          <>
            Outflow by category ·{' '}
            <span className="tabular text-fg-secondary">{fmt0(total)}</span> this month
          </>
        }
      />
      {total === 0 ? (
        <div className="empty-state">No spending recorded for {scopeLabel(scope)}.</div>
      ) : (
        <div className="grid grid-cols-2 gap-6 max-md:grid-cols-1">
          <div className="relative h-[320px] max-md:h-[220px] self-center overflow-visible [&_.recharts-wrapper]:overflow-visible [&_svg]:overflow-visible">
            <DonutCenterEmoji
              emoji={hovered ? splitCategoryEmoji(hovered).emoji : null}
              size="md"
            />
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  animationDuration={1000}
                  innerRadius="60%"
                  outerRadius="98%"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={0.5}
                  stroke="var(--berg-bg-elevated)"
                  strokeWidth={1}
                  onMouseEnter={(_, index) => setHovered(data[index]?.name ?? null)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {data.map((d) => {
                    const isFocused = hovered === null || hovered === d.name;
                    return (
                      <Cell
                        key={d.name}
                        fill={d.color}
                        fillOpacity={isFocused ? 1 : 0.2}
                        style={{ transition: 'fill-opacity 0.22s ease' }}
                      />
                    );
                  })}
                </Pie>
                <Tooltip
                  formatter={(v) => fmt0(typeof v === 'number' ? v : 0)}
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                  cursor={{ fill: 'transparent' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="self-center">
            <BreakdownLegend
              items={data.map((d) => ({
                key: d.name,
                label: d.label,
                value: d.value,
                color: d.color,
              }))}
              total={total}
              formatValue={fmt0}
              collapseAfter={isStacked ? 6 : Infinity}
              hovered={hovered}
              onHoverChange={setHovered}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Single budget-progress tile: a category-colored progress ring (with red
// over-budget overlay) sized as the tile's focal element, plus a one-line
// "$X left" / "$X over" readout. Clicking navigates to the Budget page
// pre-expanded to this category via the `?cat=` query param.
function BudgetProgressTile({
  category,
  scope,
  actual,
  color,
}: {
  category: BudgetCategory;
  scope: Scope;
  actual: number | undefined;
  color: string;
}) {
  const { emoji, name: bareName } = splitCategoryEmoji(category.name);
  const Icon = emoji ? null : getCategoryIcon(category.name);
  const planned = category[scope];
  const hasActual = actual !== undefined;
  const spentPct =
    hasActual && planned > 0 ? Math.min(100, (actual / planned) * 100) : 0;
  const overBudget = hasActual && planned > 0 && actual > planned;
  const overPct = overBudget
    ? Math.min(100, ((actual - planned) / planned) * 100)
    : 0;
  const remaining = hasActual ? planned - actual : planned;
  const isOver = hasActual && remaining < 0;

  // The $ amount stays in the donut's highlight color (category, or red
  // when over). On hover, instead of brightening the number we promote
  // the "Left" / "Over" label below — white when on track, red when over —
  // so the hover signals which side of budget you're on.
  const amountColor = isOver ? 'var(--berg-red)' : color;
  return (
    <Link
      to={`/spending?cat=${encodeURIComponent(bareName)}`}
      title={bareName}
      className="card-flush group flex flex-col items-center gap-2.5 px-3 py-4 hover:border-border-strong transition-colors no-underline text-inherit"
    >
      <CategoryProgressRing
        emoji={emoji}
        Icon={Icon}
        spentPct={spentPct}
        overPct={overPct}
        color={color}
        showRing={hasActual}
        size={60}
        strokeWidth={3}
      />
      <div className="tabular text-sm font-semibold flex flex-col items-center gap-1 leading-none">
        {hasActual ? (
          <>
            <span style={{ color: amountColor } as CSSProperties}>
              {fmt0(Math.abs(remaining))}
            </span>
            <span
              className={clsx(
                'text-xs font-medium transition-colors group-hover:text-fg',
                isOver ? 'text-red' : 'text-fg-tertiary',
              )}
            >
              {isOver ? 'Over' : 'Left'}
            </span>
          </>
        ) : (
          <span className="text-fg-tertiary">—</span>
        )}
      </div>
    </Link>
  );
}

// Grid of per-category progress tiles, gated on the same conditions as the
// Budget page's actuals rings (transactions feature enabled + Plaid items
// connected + scope resolvable). Sub-item actuals are rolled up to their
// parent category — same logic as the Budget page so the two views agree.
function BudgetProgressTiles({
  categories,
  scope,
}: {
  categories: BudgetCategory[];
  scope: Scope;
}) {
  const { enabled: transactionsEnabled } = useTransactionsEnabled();
  const plaid = usePlaidItems();
  const household = useHousehold();

  const actualsScope = useMemo<TransactionsScope | undefined>(() => {
    if (scope === 'household') return undefined;
    const viewAsUserId =
      scope === 'dakota' ? household.ids.dakota : household.ids.natalia;
    if (viewAsUserId == null) return undefined;
    return { viewAsUserId };
  }, [scope, household.ids]);

  const actuals = useBudgetActuals(actualsScope);

  // Roll the raw per-budget_category totals up to the bare top-level name
  // each category lives under. Matches the Budget page's actualsByCategory
  // so the same number renders in both places.
  //
  // Auto-actual items (green-flagged rows in the sheet — 401k contributions,
  // payroll-deducted HSA, etc.) get their planned amount layered on top
  // since they don't produce a Plaid transaction. The tile's progress ring
  // then reads as fully spent for those items without manual reconciliation,
  // matching the Budget page's category bar.
  const actualsByCategory = useMemo(() => {
    const result = new Map<string, number>();
    const itemToParent = new Map<string, string>();
    const knownBareNames = new Set<string>();
    for (const c of categories) {
      const bareCat = splitCategoryEmoji(c.name).name;
      knownBareNames.add(bareCat);
      for (const item of c.items) {
        if (item.name) itemToParent.set(item.name, bareCat);
      }
    }
    for (const [stored, amount] of Object.entries(actuals.totals)) {
      const bareStored = splitCategoryEmoji(stored).name;
      const parent = knownBareNames.has(bareStored)
        ? bareStored
        : itemToParent.get(stored) ?? itemToParent.get(bareStored);
      if (!parent) continue;
      result.set(parent, (result.get(parent) ?? 0) + amount);
    }
    for (const c of categories) {
      const bareCat = splitCategoryEmoji(c.name).name;
      for (const item of c.items) {
        if (!item.autoActual) continue;
        const planned = item[scope];
        if (planned === 0) continue;
        result.set(bareCat, (result.get(bareCat) ?? 0) + planned);
      }
    }
    return result;
  }, [categories, actuals.totals, scope]);

  const colorMap = useMemo(
    () => assignCategoryColors(categories.map((c) => c.name)),
    [categories],
  );

  // Sort by household amount (not the scoped value) so the tile order is
  // locked across scope changes — switching to Dakota or Natalia hides any
  // tile where that person has $0, but the remaining tiles stay in the same
  // big-to-small household order instead of shuffling per scope.
  const visible = useMemo(
    () =>
      [...categories]
        .filter((c) => c[scope] !== 0)
        .sort((a, b) => b.household - a.household),
    [categories, scope],
  );

  const scopeResolvable =
    scope === 'household' ||
    (scope === 'dakota' && household.ids.dakota != null) ||
    (scope === 'natalia' && household.ids.natalia != null);

  const show =
    transactionsEnabled &&
    plaid.status === 'ready' &&
    plaid.householdHasItems &&
    scopeResolvable;

  if (!show || visible.length === 0) return null;

  // Row of standalone tiles — each tile is its own card-flush element, not
  // wrapped in an outer card. Mobile pins to a 4-col grid so the tiles stay
  // legible without a long horizontal scan. Desktop falls back to auto-fit +
  // minmax so the row packs as many tiles as fit at min 100px wide; remaining
  // space divides evenly among the last row's tiles.
  return (
    <div className="grid gap-3 max-md:gap-1.5 grid-cols-4 md:grid-cols-[repeat(auto-fit,minmax(100px,1fr))]">
      {visible.map((c) => {
        const bareName = splitCategoryEmoji(c.name).name;
        const actualForCat =
          actuals.status === 'ready'
            ? actualsByCategory.get(bareName) ?? 0
            : undefined;
        return (
          <BudgetProgressTile
            key={c.name}
            category={c}
            scope={scope}
            actual={actualForCat}
            color={colorMap.get(c.name) ?? 'var(--berg-neutral)'}
          />
        );
      })}
    </div>
  );
}

// Wraps the spending donut + monthly in/out/remaining 3-up so a single
// scope toggle (rendered on the donut card) re-scopes both cards.
function CashFlowGroup({
  spending,
  netMonthly,
  gap,
  budgetCategories,
}: {
  spending: DashboardData['spending'];
  netMonthly: Tri;
  gap: Tri;
  budgetCategories: BudgetCategory[];
}) {
  const { scope } = useScope();
  return (
    <div className="space-y-5">
      <CategoryDonut
        rows={spending.byCategory}
        totals={spending.totals}
        scope={scope}
      />
      <BudgetProgressTiles categories={budgetCategories} scope={scope} />
      <MonthlyTotals
        outTotal={spending.totals}
        inTotal={netMonthly}
        gap={gap}
        scope={scope}
      />
    </div>
  );
}

export function DashboardPage() {
  const { data, isLoading, error, lastFetched, refresh } = useDashboard();
  const { sync: syncPlaid } = usePlaidAutoSync();

  const gap = useMemo<Tri>(() => {
    if (!data) return { household: 0, dakota: 0, natalia: 0 };
    const r = data.income.remaining;
    if (r.household || r.dakota || r.natalia) return r;
    return {
      household: data.income.netMonthly.household - data.spending.totals.household,
      dakota: data.income.netMonthly.dakota - data.spending.totals.dakota,
      natalia: data.income.netMonthly.natalia - data.spending.totals.natalia,
    };
  }, [data]);

  return (
    <div className="page-container space-y-10">
      <header className="page-header">
        <div>
          <h1 className="page-title">Household</h1>
          <p className="page-subtitle">
            {lastFetched
              ? `Updated ${formatDate(new Date(lastFetched), {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}`
              : isLoading
                ? 'Syncing…'
                : 'Press refresh to load'}
          </p>
        </div>
        {data && (
          <button
            onClick={() => {
              void refresh();
              syncPlaid();
            }}
            disabled={isLoading}
            className="btn btn-secondary"
          >
            {isLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      </header>

      {error && <div className="alert-error">Couldn't load the sheet: {error}</div>}

      {data && (
        <>
          {/* Order: upcoming bills (urgent) → budget (this month's flow)
              → net worth (current snapshot + projection) → retirement
              (long-term horizon). All three groups read scope from the
              global ScopeProvider, so the topbar toggle re-scopes the
              entire dashboard at once. */}
          <BillAlerts
            bills={data.bills.items}
            budgetCategories={data.budget.categories}
          />
          <CashFlowGroup
            spending={data.spending}
            netMonthly={data.income.netMonthly}
            gap={gap}
            budgetCategories={data.budget.categories}
          />
          <GiftAlerts gifts={data.gifts.items} />
          <NetWorthGroup
            netWorth={data.netWorth.netWorth}
            assets={data.netWorth.assetsTotal}
            liabilities={data.netWorth.liabilitiesTotal}
            retirement={data.retirement}
            monthlyDebtPaydown={data.debts.totals.minimumPrincipal}
            monthlySavings={data.budget.summary.monthlySavings}
          />
          <RetirementGroup
            retirement={data.retirement}
            annualExpenses={data.budget.summary.annualExpenses}
          />
        </>
      )}
    </div>
  );
}
